import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalChangeCompany.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import { showSuccess, showError } from "../../services/toast.service";
import CompanySearchBar from "../CompanySearchBar";

const ModalChangeCompany = ({ visible, onClose, onSave, folderId, currentCompanyId }) => {
  const modalRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Gère le défilement du corps
  useEffect(() => {
    const targetElement = modalRef.current || document.querySelector("#modal-change-company");

    if (visible) {
      if (targetElement) {
        disableBodyScroll(targetElement);
      }
    } else {
      clearAllBodyScrollLocks();
      setSelectedCompany(null);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [visible]);

  // Gère la touche 'Échap' pour fermer la modale
  const escFunction = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [escFunction]);

  // Gère la sélection d'une entreprise existante
  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
  };

  // Gère l'association d'une entreprise existante au dossier
  const handleAssignCompany = async () => {
    if (!selectedCompany || !folderId) return;

    setIsSubmitting(true);
    try {
      // Importer le service updateDraftFolder
      const { updateDraftFolder } = await import('../../services/folder.service');
      
      await updateDraftFolder(folderId, {
        company_id: selectedCompany.id
      });

      showSuccess("Entreprise associée avec succès");
      
      if (onSave) {
        onSave(selectedCompany);
      }
      
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'association de l'entreprise:", error);
      showError("Erreur lors de l'association de l'entreprise");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ne rend le portail que si la modale est visible
  if (!visible) {
    return null;
  }

  return createPortal(
    <>
      <div id="modal-change-company" className={styles.modal} ref={modalRef}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose} aria-label="Fermer la modale">
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer}>
          <Card
            className={styles.card}
            title="Modifier l'entreprise"
            classTitle="title-blue"
          >
            <div className={styles.container}>
              <CompanySearchBar
                label="Rechercher une entreprise :"
                onSelectCompany={handleSelectCompany}
                showDetails={true}
                initialCompanyId={visible ? currentCompanyId : null}
                key={visible ? 'open' : 'closed'} // Force re-render when modal visibility changes
              />

              <div className={styles.divider} />

              {/* Boutons d'action */}
              <div className={styles.btnWrap}>
                <button 
                  type="button" 
                  className="button-stroke" 
                  onClick={onClose} 
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={handleAssignCompany}
                  disabled={isSubmitting || !selectedCompany}
                >
                  {isSubmitting ? "Modification en cours..." : "Modifier l'entreprise"}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ModalChangeCompany;